import { useReducer } from 'react'

import { useMutation, useQuery } from '@apollo/client'
import omit from 'lodash/omit'
import reduce from 'lodash/reduce'
import { useSelector } from 'react-redux'

import { CreateOrphanRetailerDocument } from '__generated__/atlas-types'

import { GlobalIdTypes, toGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import {
  getAccountId,
  isAccountTypeBrand,
  isLiteRetailer as isLiteRetailerSelector,
} from 'store/currentUser/selectors'

import { VARIANTS_IN_CART } from './ProductCatalog.queries'
import { convertToVariantIdDictionary } from './ProductCatalog.transformations'

/**
 * Reducer to keep track of the dictionary
 * of variants ids per productIds. Supports two actions:
 * toggle variant id on a productId or add or remove all
 * variantIds for a productId.
 * When a productId's array ends up empty the property
 * that holds the variant ids will be removed from the
 * reducer's state.
 */
const useNotedVariantsReducer = (state, action) => {
  const {
    payload: { productId, variantId, variants, warehouseId },
  } = action

  switch (action.type) {
    case 'toggle_variant':
      if (!productId || !variantId || !warehouseId) {
        return state
      }
      // Remove the id if it's already there.
      const existVariant = state?.[warehouseId]?.[productId]?.find(
        (id) => id === variantId,
      )
      if (existVariant) {
        const filteredVariants = state?.[warehouseId]?.[productId]?.filter(
          (notedVariant) => notedVariant !== variantId,
        )
        if (!filteredVariants?.length) {
          return {
            ...state,
            [warehouseId]: omit(state[warehouseId], productId),
          }
        }
        return {
          ...state,
          [warehouseId]: {
            ...state[warehouseId],
            [productId]: filteredVariants,
          },
        }
      }
      // Add the id and the product id property.
      else {
        return {
          ...state,
          [warehouseId]: {
            ...state[warehouseId],
            [productId]: [
              ...(state[warehouseId]?.[productId] ?? []),
              variantId,
            ],
          },
        }
      }

    case 'toggle_all_variants':
      if (!productId || !variants?.length || !warehouseId) {
        return state
      }
      // Remove the productId from the obj keys if we're toggling off
      if (state?.[warehouseId]?.[productId]?.length) {
        return {
          ...state,
          [warehouseId]: omit(state[warehouseId], productId),
        }
      } else {
        return {
          ...state,
          [warehouseId]: {
            ...state[warehouseId],
            [productId]: variants,
          },
        }
      }

    case 'clear_all_variants':
      return omit(state, warehouseId)
    default:
      return state
  }
}

/**
 * Hook that stores all the noted elements state via a dictionary
 * of product ids that point to an array of variantIds.
 */
export const useNotedVariants = () => {
  const [state, dispatch] = useReducer(useNotedVariantsReducer, {})

  const getArrayOfVariantIds = () =>
    reduce(
      state,
      (ids, products) => [
        ...ids,
        ...Object.values(products)?.reduce(
          (acc, variantIds) => [...acc, ...variantIds],
          [],
        ),
      ],
      [],
    )

  const toggleVariant = ({ productId, variantId, warehouseId }) => {
    dispatch({
      type: 'toggle_variant',
      payload: { productId, variantId, warehouseId },
    })
  }

  const toggleAllVariants = ({ productId, variants, warehouseId }) => {
    dispatch({
      type: 'toggle_all_variants',
      payload: { productId, variants, warehouseId },
    })
  }

  const clearVariants = ({ warehouseId }) => {
    dispatch({
      type: 'clear_all_variants',
      payload: { warehouseId },
    })
  }

  return {
    notedVariants: state,
    toggleVariant,
    toggleAllVariants,
    clearVariants,
    getArrayOfVariantIds,
  }
}

export const useVariantsInCart = (brandId, retailerId, orphanRetailerId) => {
  const enableCatalogNoting = useEnableCatalogNoting()
  const { data, refetch } = useQuery(VARIANTS_IN_CART, {
    client: atlasClient,
    variables: {
      brandId,
      retailerIds: orphanRetailerId ? undefined : [retailerId],
      orphanRetailerIds: retailerId ? undefined : [orphanRetailerId],
    },
    skip:
      !brandId || (!retailerId && !orphanRetailerId) || !enableCatalogNoting,
  })

  // Here goes some data transformations.
  return {
    variantsInCart: convertToVariantIdDictionary(data?.variantsInCart),
    refetchVariantsInCart: refetch,
  }
}

export const useEnableCatalogNoting = () => {
  const isBrand = useSelector(isAccountTypeBrand)
  const isLiteRetailer = useSelector(isLiteRetailerSelector)
  return isBrand || isLiteRetailer
}

export const useCreateOrphanRetailer = () => {
  const accountId = useSelector((state) => getAccountId(state))
  const [mutate] = useMutation(CreateOrphanRetailerDocument, {
    client: atlasClient,
  })

  const createOrphanRetailer = async ({
    retailerName,
    retailerEmail,
    connectionProcessed = false,
  }) => {
    let mutationError = ''
    let orphanRetailer = null
    try {
      const response = await mutate({
        variables: {
          accountId: toGlobalId(GlobalIdTypes.Account, accountId) ?? '',
          retailerName,
          retailerEmail,
          connectionProcessed,
        },
      })
      orphanRetailer = response.data?.createOrphanRetailer
      if (!orphanRetailer?.id) {
        mutationError = 'Failed to create orphan retailer'
      }
    } catch (error) {
      mutationError = error
    }

    if (!mutationError) {
      return {
        orphanRetailer,
      }
    }

    console.error(`[Create Orphan Retailer] Error: ${mutationError}`)
    return {
      orphanRetailer: null,
    }
  }

  return {
    createOrphanRetailer,
  }
}
